import React from 'react';
import { Calendar, Clock, Scissors, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../data/translations';

export const BOOKING_URL = 'https://hotdogssalonzapse.linky.rs';

const Booking: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const steps = [
    { icon: Scissors, label: t.bookingStep1 },
    { icon: Calendar, label: t.bookingStep2 },
    { icon: Clock, label: t.bookingStep3 },
  ];

  return (
    <section id="booking" className="py-12 sm:py-16 lg:py-24 bg-warm-gray relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center space-y-3 sm:space-y-4 mb-10 sm:mb-14 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-light-gray">
            <Sparkles className="h-4 w-4 text-black" />
            <span className="text-xs sm:text-sm font-sans font-medium text-gray-600 tracking-wide uppercase">
              {t.bookingBadge}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-black">
            {t.bookingTitle}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 font-sans font-light max-w-2xl mx-auto leading-relaxed">
            {t.bookingSubtitle}
          </p>
        </div>

        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-light-gray overflow-hidden animate-slide-up">
          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-light-gray">
            {steps.map((step, index) => (
              <div
                key={step.label}
                className="flex flex-col items-center text-center px-6 py-8 sm:py-10 gap-4"
              >
                <div className="relative">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-black rounded-2xl flex items-center justify-center shadow-lg">
                    <step.icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-warm-gray border border-light-gray rounded-full flex items-center justify-center text-xs font-sans font-bold text-black">
                    {index + 1}
                  </span>
                </div>
                <p className="text-sm sm:text-base font-sans font-medium text-black leading-snug max-w-[200px]">
                  {step.label}
                </p>
              </div>
            ))}
          </div>

          <div className="px-6 sm:px-10 lg:px-14 py-8 sm:py-10 bg-warm-gray border-t border-light-gray">
            <div className="flex flex-col items-center gap-5 sm:gap-6">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 w-full sm:w-auto min-w-[280px] px-10 py-4 sm:py-5 bg-black hover:bg-gray-800 text-white font-sans font-semibold text-base sm:text-lg rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl shadow-xl touch-target"
              >
                <Calendar className="h-5 w-5 sm:h-6 sm:w-6 transition-transform duration-300 group-hover:scale-110" />
                {t.bookAppointment}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <p className="text-xs sm:text-sm text-gray-500 font-sans text-center max-w-md leading-relaxed">
                {t.bookingNote}
              </p>
              <p className="text-sm text-gray-600 font-sans">
                {t.bookingViaPhone}{' '}
                <a
                  href="tel:+381653266000"
                  className="font-semibold text-black hover:underline"
                >
                  +381 65 326 6000
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Booking;
